/**
 * Build-time map of optimized image variants.
 *
 * scripts/optimize-images.mjs writes src/assets/img/<group>/<base>-<width>.<ext>.
 * Vite content-hashes anything imported from src/, emits it to /assets/, and it
 * inherits the existing immutable cache rule — so no _headers file is needed and
 * no unhashed path gains immutable.
 *
 * Keys are "<group>/<base>", e.g. "archives/origin". Legacy paths such as
 * "/images/archives/origin.png" (which the API returns, and which we cannot
 * rename) resolve to the same key via variantKey().
 */
const modules = import.meta.glob<string>('../assets/img/**/*.{avif,webp,png,jpg}', {
  eager: true,
  import: 'default',
  query: '?url',
})

export type VariantSet = {
  avif: Record<number, string>
  webp: Record<number, string>
  fallback: Record<number, string>
  widths: number[]
}

const registry: Record<string, VariantSet> = {}

for (const [file, url] of Object.entries(modules)) {
  // ../assets/img/archives/origin-152.avif -> group "archives", base "origin", width 152
  const m = file.match(/\/assets\/img\/([^/]+)\/(.+)-(\d+)\.(avif|webp|png|jpg)$/)
  if (!m) continue
  const [, group, base, widthStr, ext] = m
  const width = Number(widthStr)
  const key = `${group}/${base}`
  const set = (registry[key] ||= { avif: {}, webp: {}, fallback: {}, widths: [] })
  if (ext === 'avif') set.avif[width] = url
  else if (ext === 'webp') set.webp[width] = url
  else set.fallback[width] = url
  if (!set.widths.includes(width)) set.widths.push(width)
}

for (const set of Object.values(registry)) set.widths.sort((a, b) => a - b)

/** "/images/archives/origin.png" | "archives/origin" -> "archives/origin" */
export function variantKey(src: string): string {
  const m = src.match(/\/images\/([^/]+)\/([^/]+?)(?:-\d+)?\.(?:avif|webp|png|jpe?g)$/i)
  if (m) return `${m[1]}/${m[2]}`
  // loose files such as /images/archive-network-seal.png live under the "images" group
  const single = src.match(/\/images\/([^/]+?)(?:-\d+)?\.(?:avif|webp|png|jpe?g)$/i)
  if (single) return `images/${single[1]}`
  return src
}

export function getVariants(src: string | undefined): VariantSet | null {
  if (!src) return null
  if (/^https?:\/\//i.test(src) && !src.includes('thearchivenetwork.com')) return null
  return registry[variantKey(src)] ?? null
}

export const variantRegistry = registry
